import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    Badge,
    Button,
    Spinner,
    Alert,
    Modal,
    Form,
    Tabs,
    Tab,
    Pagination,
    ListGroup,
    Image,
} from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';

import { useAdminApi } from '@/shared/services/adminApi';
import './Admin.css';

// Default avatar placeholder - inline SVG data URI for user icon
const DEFAULT_AVATAR =
    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="40" height="40" ' +
    'viewBox="0 0 40 40"%3E%3Ccircle cx="20" cy="20" r="20" fill="%23e9ecef"/%3E' +
    '%3Ccircle cx="20" cy="15" r="7" fill="%23adb5bd"/%3E' +
    '%3Cellipse cx="20" cy="32" rx="12" ry="9" fill="%23adb5bd"/%3E%3C/svg%3E';

export default function AdminOrganizations() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(
        searchParams.get('filter') === 'unowned' ? 'unowned' : 'pending'
    );
    const [organizations, setOrganizations] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        pages: 1,
        total: 0,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Review modal state
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [reviewAction, setReviewAction] = useState('');
    const [adminNotes, setAdminNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Assign member modal state
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assignOrg, setAssignOrg] = useState(null);
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [searchingUsers, setSearchingUsers] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [assignRole, setAssignRole] = useState('owner');

    const adminApi = useAdminApi();

    const loadOrganizations = useCallback(
        async (page = 1) => {
            try {
                setLoading(true);
                setError('');

                let data;
                if (activeTab === 'unowned') {
                    data = await adminApi.fetchUnownedOrganizations({
                        page,
                        limit: 10,
                    });
                } else {
                    data = await adminApi.fetchPendingOrganizations({
                        page,
                        limit: 10,
                    });
                }

                setOrganizations(data.organizations || []);
                setPagination(
                    data.pagination || { page: 1, pages: 1, total: 0 }
                );
            } catch (err) {
                setError('Failed to load organizations');
                console.error(err);
            } finally {
                setLoading(false);
            }
        },
        [adminApi, activeTab]
    );

    useEffect(() => {
        loadOrganizations(1);
    }, [loadOrganizations]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSearchParams(tab === 'unowned' ? { filter: 'unowned' } : {});
    };

    // User search with debounce
    useEffect(() => {
        if (!userSearchQuery || userSearchQuery.length < 2) {
            setUserSearchResults([]);
            return;
        }

        const searchTimeout = setTimeout(async () => {
            try {
                setSearchingUsers(true);
                const data = await adminApi.searchUsers(userSearchQuery);
                setUserSearchResults(data.users || []);
            } catch (err) {
                console.error('User search error:', err);
                setUserSearchResults([]);
            } finally {
                setSearchingUsers(false);
            }
        }, 300);

        return () => clearTimeout(searchTimeout);
    }, [userSearchQuery, adminApi]);

    const openAssignModal = (org) => {
        setAssignOrg(org);
        setUserSearchQuery('');
        setUserSearchResults([]);
        setSelectedUser(null);
        setAssignRole('owner');
        setShowAssignModal(true);
    };

    const handleAssignSubmit = async () => {
        if (!assignOrg || !selectedUser) return;

        try {
            setSubmitting(true);
            await adminApi.assignMember(
                assignOrg._id,
                selectedUser.firebaseUID,
                assignRole
            );

            setSuccess(
                `${assignRole.charAt(0).toUpperCase() + assignRole.slice(1)} assigned successfully`
            );
            setShowAssignModal(false);
            loadOrganizations(pagination.page);

            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to assign member');
        } finally {
            setSubmitting(false);
        }
    };

    const openReviewModal = (org, action) => {
        setSelectedOrg(org);
        setReviewAction(action);
        setAdminNotes('');
        setShowReviewModal(true);
    };

    const handleReviewSubmit = async () => {
        if (!selectedOrg || !reviewAction) return;

        try {
            setSubmitting(true);
            await adminApi.updateOrganizationStatus(
                selectedOrg._id,
                reviewAction,
                adminNotes
            );

            setSuccess(
                `Organization ${reviewAction === 'approved' ? 'approved' : 'rejected'} successfully`
            );
            setShowReviewModal(false);
            loadOrganizations(pagination.page);

            // Clear success after 3 seconds
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(
                err.response?.data?.message ||
                    'Failed to update organization status'
            );
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <>
            <Helmet>
                <title>Manage Organizations | Admin | RILE Connect</title>
            </Helmet>
            <Container className="py-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h1 className="text-primary mb-0">Manage Organizations</h1>
                    <Link to="/admin" className="btn btn-outline-secondary">
                        Back to Admin
                    </Link>
                </div>

                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                <Tabs
                    activeKey={activeTab}
                    onSelect={handleTabChange}
                    className="mb-4"
                >
                    <Tab eventKey="pending" title="Pending Approval">
                        {/* Content rendered below */}
                    </Tab>
                    <Tab eventKey="unowned" title="Unowned Organizations">
                        {/* Content rendered below */}
                    </Tab>
                </Tabs>

                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : organizations.length === 0 ? (
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="text-center py-5">
                            <p className="text-muted mb-0">
                                {activeTab === 'pending'
                                    ? 'No organizations pending approval'
                                    : 'No unowned organizations'}
                            </p>
                        </Card.Body>
                    </Card>
                ) : (
                    <>
                        <Row>
                            {organizations.map((org) => (
                                <Col lg={6} key={org._id} className="mb-4">
                                    <Card className="h-100 border-0 shadow-sm">
                                        <Card.Body>
                                            <div className="d-flex gap-3">
                                                {org.imageUrl ? (
                                                    <img
                                                        src={org.imageUrl}
                                                        alt={org.name}
                                                        className="rounded org-thumbnail-md"
                                                        onError={(e) => {
                                                            e.target.style.display =
                                                                'none';
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="rounded bg-light d-flex align-items-center justify-content-center org-placeholder-md">
                                                        <span className="text-muted">
                                                            {org.name.charAt(0)}
                                                        </span>
                                                    </div>
                                                )}
                                                <div className="flex-grow-1">
                                                    <h5 className="mb-1">
                                                        {org.name}
                                                    </h5>
                                                    <Badge
                                                        bg="primary"
                                                        className="me-2"
                                                    >
                                                        {org.primarySector}
                                                    </Badge>
                                                    {org.regionsServed
                                                        ?.slice(0, 2)
                                                        .map((r) => (
                                                            <Badge
                                                                bg="secondary"
                                                                key={r}
                                                                className="me-1"
                                                            >
                                                                {r}
                                                            </Badge>
                                                        ))}
                                                </div>
                                            </div>

                                            <p className="text-muted mt-3 mb-2 small">
                                                {org.description?.substring(
                                                    0,
                                                    150
                                                )}
                                                {org.description?.length >
                                                    150 && '...'}
                                            </p>

                                            <div className="small text-muted mb-3">
                                                {org.email && (
                                                    <div>
                                                        Email: {org.email}
                                                    </div>
                                                )}
                                                {org.website && (
                                                    <div>
                                                        Website:{' '}
                                                        <a
                                                            href={org.website}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {org.website}
                                                        </a>
                                                    </div>
                                                )}
                                                <div>
                                                    Submitted:{' '}
                                                    {formatDate(org.createdAt)}
                                                </div>
                                            </div>

                                            <div className="d-flex gap-2">
                                                {activeTab === 'pending' ? (
                                                    <>
                                                        <Button
                                                            variant="success"
                                                            size="sm"
                                                            onClick={() =>
                                                                openReviewModal(
                                                                    org,
                                                                    'approved'
                                                                )
                                                            }
                                                        >
                                                            Approve
                                                        </Button>
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() =>
                                                                openReviewModal(
                                                                    org,
                                                                    'rejected'
                                                                )
                                                            }
                                                        >
                                                            Reject
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Button
                                                            variant="primary"
                                                            size="sm"
                                                            onClick={() =>
                                                                openAssignModal(
                                                                    org
                                                                )
                                                            }
                                                        >
                                                            Assign Member
                                                        </Button>
                                                        <Link
                                                            to={`/organizations/${org._id}`}
                                                            className="btn btn-outline-secondary btn-sm"
                                                        >
                                                            View
                                                        </Link>
                                                    </>
                                                )}
                                                {activeTab === 'pending' && (
                                                    <Link
                                                        to={`/organizations/${org._id}`}
                                                        className="btn btn-outline-secondary btn-sm"
                                                        target="_blank"
                                                    >
                                                        Preview
                                                    </Link>
                                                )}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>

                        {/* Pagination */}
                        {pagination.pages > 1 && (
                            <div className="d-flex justify-content-center">
                                <Pagination>
                                    <Pagination.First
                                        onClick={() => loadOrganizations(1)}
                                        disabled={pagination.page === 1}
                                    />
                                    <Pagination.Prev
                                        onClick={() =>
                                            loadOrganizations(
                                                pagination.page - 1
                                            )
                                        }
                                        disabled={pagination.page === 1}
                                    />
                                    {[...Array(pagination.pages)].map(
                                        (_, i) => (
                                            <Pagination.Item
                                                key={i + 1}
                                                active={
                                                    i + 1 === pagination.page
                                                }
                                                onClick={() =>
                                                    loadOrganizations(i + 1)
                                                }
                                            >
                                                {i + 1}
                                            </Pagination.Item>
                                        )
                                    )}
                                    <Pagination.Next
                                        onClick={() =>
                                            loadOrganizations(
                                                pagination.page + 1
                                            )
                                        }
                                        disabled={
                                            pagination.page === pagination.pages
                                        }
                                    />
                                    <Pagination.Last
                                        onClick={() =>
                                            loadOrganizations(pagination.pages)
                                        }
                                        disabled={
                                            pagination.page === pagination.pages
                                        }
                                    />
                                </Pagination>
                            </div>
                        )}
                    </>
                )}

                {/* Review Modal */}
                <Modal
                    show={showReviewModal}
                    onHide={() => setShowReviewModal(false)}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {reviewAction === 'approved'
                                ? 'Approve Organization'
                                : 'Reject Organization'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {selectedOrg && (
                            <>
                                <p>
                                    <strong>Organization:</strong>{' '}
                                    {selectedOrg.name}
                                </p>
                                <p>
                                    <strong>Sector:</strong>{' '}
                                    {selectedOrg.primarySector}
                                </p>

                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Admin Notes (optional)
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={adminNotes}
                                        onChange={(e) =>
                                            setAdminNotes(e.target.value)
                                        }
                                        placeholder={
                                            reviewAction === 'rejected'
                                                ? 'Reason for rejection...'
                                                : 'Any notes about this approval...'
                                        }
                                    />
                                </Form.Group>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={() => setShowReviewModal(false)}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant={
                                reviewAction === 'approved'
                                    ? 'success'
                                    : 'danger'
                            }
                            onClick={handleReviewSubmit}
                            disabled={submitting}
                        >
                            {submitting ? (
                                <Spinner animation="border" size="sm" />
                            ) : reviewAction === 'approved' ? (
                                'Approve'
                            ) : (
                                'Reject'
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Assign Member Modal */}
                <Modal
                    show={showAssignModal}
                    onHide={() => setShowAssignModal(false)}
                    size="lg"
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Assign Member</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {assignOrg && (
                            <>
                                <p>
                                    <strong>Organization:</strong>{' '}
                                    {assignOrg.name}
                                </p>

                                <Form.Group className="mb-3">
                                    <Form.Label>Search Members</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search by name or email..."
                                        value={userSearchQuery}
                                        onChange={(e) =>
                                            setUserSearchQuery(e.target.value)
                                        }
                                    />
                                    <Form.Text className="text-muted">
                                        Type at least 2 characters to search
                                    </Form.Text>
                                </Form.Group>

                                {searchingUsers && (
                                    <div className="text-center py-3">
                                        <Spinner
                                            animation="border"
                                            size="sm"
                                            variant="primary"
                                        />
                                    </div>
                                )}

                                {userSearchResults.length > 0 && (
                                    <ListGroup className="mb-3">
                                        {userSearchResults.map((user) => (
                                            <ListGroup.Item
                                                key={user._id}
                                                action
                                                active={
                                                    selectedUser?._id ===
                                                    user._id
                                                }
                                                onClick={() =>
                                                    setSelectedUser(user)
                                                }
                                                className="d-flex align-items-center"
                                            >
                                                <Image
                                                    src={
                                                        user.profilePicture ||
                                                        DEFAULT_AVATAR
                                                    }
                                                    alt={`${user.name?.first || ''} ${user.name?.last || ''}`}
                                                    roundedCircle
                                                    className="me-3 profile-thumbnail-sm"
                                                />
                                                <div>
                                                    <strong>
                                                        {user.name?.first}{' '}
                                                        {user.name?.last}
                                                    </strong>
                                                    <br />
                                                    <small className="text-muted">
                                                        {user.email}
                                                    </small>
                                                </div>
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                )}

                                {userSearchQuery.length >= 2 &&
                                    !searchingUsers &&
                                    userSearchResults.length === 0 && (
                                        <Alert variant="info">
                                            No users found matching &quot;
                                            {userSearchQuery}&quot;
                                        </Alert>
                                    )}

                                {selectedUser && (
                                    <Card className="mb-3 bg-light">
                                        <Card.Body>
                                            <strong>Selected Member:</strong>
                                            <div className="d-flex align-items-center mt-2">
                                                <Image
                                                    src={
                                                        selectedUser.profilePicture ||
                                                        DEFAULT_AVATAR
                                                    }
                                                    alt={`${selectedUser.name?.first || ''} ${selectedUser.name?.last || ''}`}
                                                    roundedCircle
                                                    className="me-3 profile-thumbnail-md"
                                                />
                                                <div>
                                                    <strong>
                                                        {selectedUser.name
                                                            ?.first || ''}{' '}
                                                        {selectedUser.name
                                                            ?.last || ''}
                                                    </strong>
                                                    <br />
                                                    <small>
                                                        {selectedUser.email}
                                                    </small>
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                )}

                                <Form.Group className="mb-3">
                                    <Form.Label>Role</Form.Label>
                                    <Form.Select
                                        value={assignRole}
                                        onChange={(e) =>
                                            setAssignRole(e.target.value)
                                        }
                                    >
                                        <option value="owner">
                                            Owner - Full control and can manage
                                            all members
                                        </option>
                                        <option value="admin">
                                            Admin - Can edit organization and
                                            manage members
                                        </option>
                                        <option value="member">
                                            Member - Can view organization
                                            details
                                        </option>
                                    </Form.Select>
                                    <Form.Text className="text-muted">
                                        Organizations can have multiple owners.
                                    </Form.Text>
                                </Form.Group>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={() => setShowAssignModal(false)}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleAssignSubmit}
                            disabled={submitting || !selectedUser}
                        >
                            {submitting ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                `Assign ${assignRole.charAt(0).toUpperCase() + assignRole.slice(1)}`
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Container>
        </>
    );
}
